import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { publicApi } from "../lib/api";
import { useMemberAuth } from "../context/MemberAuthContext";
import { toast } from "../hooks/use-toast";
import { ArrowRight, ShieldCheck, XCircle, Loader2 } from "lucide-react";

const Activate = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const nextParam = new URLSearchParams(loc.search).get("next") || "";
  const { activate } = useMemberAuth();
  const [state, setState] = useState({ loading: true, error: null, info: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    publicApi.get(`/activate/${token}`).then(({ data }) => {
      if (mounted) setState({ loading: false, error: null, info: data });
    }).catch((err) => {
      const detail = err?.response?.data?.detail || "This invitation link is no longer valid.";
      if (mounted) setState({ loading: false, error: detail, info: null });
    });
    return () => { mounted = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast({ title: "Password too short", description: "Use at least 8 characters." }); return; }
    if (password !== confirm) { toast({ title: "Passwords don't match" }); return; }
    setSubmitting(true);
    try {
      await activate(token, password);
      toast({ title: "You're in", description: "Your member account is active." });
      // If the activation link carried a `next` (e.g. arrived here after being
      // bounced from a message-notification email), honour it. Otherwise send
      // the new member to complete their profile.
      const dest = nextParam && nextParam.startsWith("/") ? nextParam : "/member/profile";
      nav(dest, { replace: true, state: dest === "/member/profile" ? { firstTime: true } : undefined });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not activate. Please try again.";
      toast({ title: "Activation failed", description: detail });
    } finally { setSubmitting(false); }
  };

  if (state.loading) return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="flex items-center gap-2 text-[#0d2a20]/70"><Loader2 size={18} className="animate-spin" /> Validating your invitation…</div>
    </div>
  );

  if (state.error) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="w-full max-w-[480px] rounded-lg p-8 text-center" style={{ background: '#fbf8ef' }}>
        <XCircle size={40} className="mx-auto text-[#e0684d] mb-4" />
        <h1 className="hero-title text-[32px] leading-tight mb-2">Invitation <span className="font-serif-i text-[#e0684d]">unavailable</span></h1>
        <p className="text-[14px] text-[#0d2a20]/75 mb-6">{state.error}</p>
        <Link to="/join" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-green text-[14px]">Apply to join <ArrowRight size={14} /></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <form onSubmit={submit} className="w-full max-w-[480px] rounded-lg p-8" style={{ background: '#fbf8ef' }}>
        <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: '#0f3d2e', color: '#f4efe4' }}><ShieldCheck size={18} /></div>
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Diaspora Network</div>
        <h1 className="hero-title text-[36px] leading-tight mb-2">Create your <span className="font-serif-i text-[#e0684d]">account</span></h1>
        <p className="text-[14px] text-[#0d2a20]/75 mb-6">Welcome{state.info?.name ? `, ${state.info.name.split(" ")[0]}` : ""}. Set a password to activate your membership.</p>
        <div className="space-y-4">
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Email (from your application)</label>
            <input value={state.info?.email || ""} readOnly className="w-full mt-1 px-3 py-2 rounded-md bg-[#ece5d3] border cursor-not-allowed text-[#0d2a20]/80" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
          </div>
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Password</label>
            <input required type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={8} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <div className="text-[11px] text-[#0d2a20]/55 mt-1">Minimum 8 characters.</div>
          </div>
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Confirm password</label>
            <input required type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
          </div>
          <button disabled={submitting} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60">{submitting ? "Activating…" : "Activate account"} <ArrowRight size={14} /></button>
        </div>
      </form>
    </div>
  );
};

export default Activate;
